/**
 * MonitorSetupFlow - Chat-style monitor setup with full message history
 * Smooth animations and proper error handling in chat
 */
import React, { useState, useEffect, useRef } from 'react';
import { Check, Loader2, Clock, AlertCircle, RefreshCw } from 'lucide-react';
import { apiClient } from '../../lib/api';
import { useToast } from '../../hooks/useToast';
import { useSettingsModal } from '../../contexts/SettingsModalContext';
import { parseError } from '../../utils/errorUtils';
import { CreditPill } from '../ui';
import { CREDIT_COSTS } from '../../constants/credits';
import { ScheduleConfig, ScheduleFrequency, DayOfWeek, scheduleToCron } from '../../types/monitoring';
import skylarqLogo from '../../assets/skylarq_logo_small.png';

interface MonitorSetupFlowProps {
  tableId: string;
  tableName: string;
  onComplete: (keepMessages?: boolean) => void;
  onCancel: () => void;
}

type SetupStep = 'intro' | 'configure' | 'creating' | 'done' | 'completed' | 'error';

interface ErrorInfo {
  message: string;
  details?: string;
}

const DAYS: { value: DayOfWeek; label: string; short: string }[] = [
  { value: 'mon', label: 'Monday', short: 'M' },
  { value: 'tue', label: 'Tuesday', short: 'T' },
  { value: 'wed', label: 'Wednesday', short: 'W' },
  { value: 'thu', label: 'Thursday', short: 'T' },
  { value: 'fri', label: 'Friday', short: 'F' },
  { value: 'sat', label: 'Saturday', short: 'S' },
  { value: 'sun', label: 'Sunday', short: 'S' },
];

const TIME_OPTIONS = [
  { value: 6, label: '6 AM' },
  { value: 8, label: '8 AM' },
  { value: 9, label: '9 AM' },
  { value: 12, label: '12 PM' },
  { value: 17, label: '5 PM' },
  { value: 20, label: '8 PM' },
];

export const MonitorSetupFlow: React.FC<MonitorSetupFlowProps> = ({
  tableId,
  tableName,
  onComplete,
  onCancel,
}) => {
  const { showToast } = useToast();
  const { openSettings } = useSettingsModal();
  const [step, setStep] = useState<SetupStep>('intro');
  const [schedule, setSchedule] = useState<ScheduleConfig>({
    frequency: 'daily',
    hourOfDay: 8,
    daysOfWeek: ['mon', 'wed', 'fri'],
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'Etc/UTC',
  });
  const [maxResults, setMaxResults] = useState(25);
  const [submittedConfig, setSubmittedConfig] = useState<string>('');
  const [errorInfo, setErrorInfo] = useState<ErrorInfo | null>(null);
  const [messageIndex, setMessageIndex] = useState(0);
  const [animatedMessages, setAnimatedMessages] = useState<Set<string>>(new Set());
  const bottomRef = useRef<HTMLDivElement>(null);

  // Track which messages have been animated (prevent re-animation on re-render)
  const markAnimated = (id: string) => {
    if (!animatedMessages.has(id)) {
      setAnimatedMessages(prev => new Set(prev).add(id));
    }
  };

  // Auto-scroll when step changes
  useEffect(() => {
    const timer = setTimeout(() => {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
    return () => clearTimeout(timer);
  }, [step, messageIndex]);

  // Increment message index for staggered animations
  useEffect(() => {
    if (step !== 'intro') {
      const timer = setTimeout(() => setMessageIndex(prev => prev + 1), 50);
      return () => clearTimeout(timer);
    }
  }, [step]);

  const handleDayToggle = (day: DayOfWeek) => {
    const current = schedule.daysOfWeek;
    const updated = current.includes(day)
      ? current.filter(d => d !== day)
      : [...current, day];
    if (updated.length > 0) {
      setSchedule(prev => ({ ...prev, daysOfWeek: updated }));
    }
  };

  const handleConfirm = () => {
    setStep('configure');
  };

  const getScheduleSummary = () => {
    const hour = schedule.hourOfDay;
    const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    const period = hour < 12 ? 'AM' : 'PM';

    switch (schedule.frequency) {
      case 'daily':
        return `Daily at ${hour12} ${period}`;
      case 'weekly': {
        const dayNames = schedule.daysOfWeek.map(d =>
          DAYS.find(day => day.value === d)?.label.slice(0, 3)
        ).join(', ');
        return `${dayNames} at ${hour12} ${period}`;
      }
      case 'monthly':
        return `Monthly at ${hour12} ${period}`;
      default:
        return '';
    }
  };

  const handleCreate = async () => {
    // Capture the config summary before transitioning
    setSubmittedConfig(`${getScheduleSummary()}, up to ${maxResults} results`);
    setStep('creating');
    setErrorInfo(null);

    try {
      await apiClient.createMonitor({
        lead_table_id: tableId,
        name: `${tableName} Monitor`,
        cron_expression: scheduleToCron(schedule),
        timezone: schedule.timezone,
        max_results: maxResults,
        search_behavior: 'append',
      });

      setStep('done');
      showToast('Monitor created successfully!', 'success');
    } catch (error: any) {
      console.error('Failed to create monitor:', error);

      const rawError = error?.response?.data?.detail || error?.response?.data?.error || error?.message;
      const errorString = (typeof rawError === 'string' ? rawError : '').toLowerCase();

      let errorMessage = '';
      let errorDetails = '';
      let needsUpgrade = false;

      if (errorString.includes('already') && errorString.includes('monitor')) {
        errorMessage = 'A monitor already exists for this dataset.';
        errorDetails = 'You can manage it from the Monitors section in settings.';
      } else if (errorString.includes('webset') || errorString.includes('exa')) {
        errorMessage = 'This dataset cannot be monitored.';
        errorDetails = 'Only datasets created with web search can have monitors.';
      } else if (errorString.includes('completed') || errorString.includes('status')) {
        errorMessage = 'Dataset is still processing.';
        errorDetails = 'Please wait for the dataset to finish generating before setting up a monitor.';
      } else if (errorString.includes('403') || errorString.includes('permission') || errorString.includes('subscription') || errorString.includes('paid')) {
        errorMessage = 'Monitors require a paid plan.';
        errorDetails = 'Upgrade your subscription to enable automated monitoring.';
        needsUpgrade = true;
      }

      const parsed = parseError(error);
      if (!errorMessage) {
        errorMessage = parsed.message;
        errorDetails = parsed.details || '';
        needsUpgrade = parsed.requiresUpgrade || parsed.shouldReturnToIntro || false;
      }

      if (needsUpgrade) {
        showToast(errorMessage, {
          type: 'error',
          duration: 8000,
          action: { label: 'Upgrade', onClick: () => openSettings('billing') }
        });
      } else {
        showToast(errorMessage, 'error', 5000);
      }

      setErrorInfo({ message: errorMessage, details: errorDetails });
      setStep('error');
    }
  };

  const handleRetry = () => {
    setStep('configure');
    setErrorInfo(null);
    setSubmittedConfig('');
  };

  // Assistant message component with animation (only animates once per messageId)
  const AssistantMessage: React.FC<{ children: React.ReactNode; delay?: number; messageId: string }> = ({ children, delay = 0, messageId }) => {
    const shouldAnimate = !animatedMessages.has(messageId);

    useEffect(() => {
      if (shouldAnimate) {
        const timer = setTimeout(() => markAnimated(messageId), delay + 400);
        return () => clearTimeout(timer);
      }
    }, [messageId, shouldAnimate, delay]);

    return (
      <div
        className={`flex gap-3 ${shouldAnimate ? 'animate-message-in' : ''}`}
        style={shouldAnimate ? { animationDelay: `${delay}ms` } : undefined}
      >
        <img src={skylarqLogo} alt="" className="w-7 h-7 object-contain flex-shrink-0" />
        <div className="flex-1 pt-0.5">
          <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">SKYLARQ</p>
          {children}
        </div>
      </div>
    );
  };

  // User message component with animation (only animates once per messageId)
  const UserMessage: React.FC<{ children: React.ReactNode; delay?: number; messageId: string }> = ({ children, delay = 0, messageId }) => {
    const shouldAnimate = !animatedMessages.has(messageId);

    useEffect(() => {
      if (shouldAnimate) {
        const timer = setTimeout(() => markAnimated(messageId), delay + 400);
        return () => clearTimeout(timer);
      }
    }, [messageId, shouldAnimate, delay]);

    return (
      <div
        className={`flex justify-end ${shouldAnimate ? 'animate-message-in' : ''}`}
        style={shouldAnimate ? { animationDelay: `${delay}ms` } : undefined}
      >
        <div className="bg-gray-900 text-white rounded-2xl rounded-br-sm px-4 py-2.5 max-w-[85%]">
          <p className="text-[14px]">{children}</p>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Message 1: Intro (always visible) */}
      <AssistantMessage messageId="intro">
        <p className="text-[15px] text-gray-800 leading-relaxed">
          I can automatically monitor for new matches and add them to your dataset. Want me to set that up?
        </p>
      </AssistantMessage>

      {/* Intro buttons (only if still on intro step) */}
      {step === 'intro' && (
        <div className="ml-10 flex gap-2 animate-message-in" style={{ animationDelay: '100ms' }}>
          <button
            onClick={handleConfirm}
            className="px-4 py-2 text-[13px] font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition-all active:scale-[0.98]"
          >
            Yes, set it up
          </button>
          <button
            onClick={onCancel}
            className="px-4 py-2 text-[13px] font-medium text-gray-600 hover:text-gray-900 transition-colors"
          >
            Not now
          </button>
        </div>
      )}

      {/* Message 2: User confirms (visible after intro) */}
      {step !== 'intro' && (
        <UserMessage delay={0} messageId="confirm">Yes, set it up</UserMessage>
      )}

      {/* Message 3: Config form (visible in configure step) */}
      {step !== 'intro' && step !== 'creating' && step !== 'done' && step !== 'completed' && step !== 'error' && (
        <>
          <AssistantMessage delay={150} messageId="configure">
            <p className="text-[15px] text-gray-800 leading-relaxed">
              Configure your monitoring schedule:
            </p>
          </AssistantMessage>

          {/* Config Card */}
          <div className="ml-10 mt-3 animate-message-in" style={{ animationDelay: '250ms' }}>
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-4">
              {/* Frequency */}
              <div>
                <p className="text-[11px] font-medium text-gray-500 mb-2">Frequency</p>
                <div className="flex gap-1.5">
                  {(['daily', 'weekly', 'monthly'] as ScheduleFrequency[]).map(freq => (
                    <button
                      key={freq}
                      onClick={() => setSchedule(prev => ({ ...prev, frequency: freq }))}
                      className={`flex-1 py-1.5 text-[12px] font-medium rounded-md transition-all ${
                        schedule.frequency === freq
                          ? 'bg-gray-900 text-white'
                          : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {freq.charAt(0).toUpperCase() + freq.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Days (only for weekly) */}
              {schedule.frequency === 'weekly' && (
                <div className="animate-fade-in">
                  <p className="text-[11px] font-medium text-gray-500 mb-2">Days</p>
                  <div className="flex gap-1">
                    {DAYS.map((day) => (
                      <button
                        key={day.value}
                        onClick={() => handleDayToggle(day.value)}
                        className={`w-8 h-8 text-[11px] font-medium rounded-md transition-all ${
                          schedule.daysOfWeek.includes(day.value)
                            ? 'bg-gray-900 text-white'
                            : 'bg-white text-gray-500 border border-gray-200 hover:border-gray-300'
                        }`}
                        title={day.label}
                      >
                        {day.short}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Time + Results */}
              <div className="flex gap-4">
                <div className="flex-1">
                  <p className="text-[11px] font-medium text-gray-500 mb-2">Time</p>
                  <div className="flex flex-wrap gap-1">
                    {TIME_OPTIONS.map(time => (
                      <button
                        key={time.value}
                        onClick={() => setSchedule(prev => ({ ...prev, hourOfDay: time.value }))}
                        className={`px-2.5 py-1.5 text-[11px] font-medium rounded-md transition-all ${
                          schedule.hourOfDay === time.value
                            ? 'bg-gray-900 text-white'
                            : 'bg-white text-gray-500 border border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        {time.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="w-20">
                  <p className="text-[11px] font-medium text-gray-500 mb-2">Max results</p>
                  <select
                    value={maxResults}
                    onChange={(e) => setMaxResults(Number(e.target.value))}
                    className="w-full px-2 py-1.5 text-[12px] bg-white border border-gray-200 rounded-md text-gray-700 focus:outline-none focus:border-gray-400 transition-colors"
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
              </div>

            </div>

            {/* Actions */}
            <div className="flex items-center justify-between mt-3">
              <p className="text-[12px] text-gray-500 truncate mr-3">
                {getScheduleSummary()}
              </p>
              <button
                onClick={handleCreate}
                className="flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition-colors active:scale-[0.98] flex-shrink-0"
              >
                <Clock className="w-3.5 h-3.5" />
                Start Monitoring
                <CreditPill cost={CREDIT_COSTS.LEAD_GENERATION} perLead />
              </button>
            </div>
          </div>
        </>
      )}

      {/* Message 4: User submits config (visible when creating, done, completed, or error) */}
      {(step === 'creating' || step === 'done' || step === 'completed' || step === 'error') && submittedConfig && (
        <UserMessage delay={0} messageId="submit">{submittedConfig}</UserMessage>
      )}

      {/* Message 5: Creating state (visible while creating) */}
      {step === 'creating' && (
        <AssistantMessage delay={150} messageId="creating">
          <div className="flex items-center gap-2">
            <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
            <p className="text-[15px] text-gray-600">Setting up your monitor...</p>
          </div>
        </AssistantMessage>
      )}

      {/* Message 6: Success state (visible when done or completed) */}
      {(step === 'done' || step === 'completed') && (
        <AssistantMessage delay={150} messageId="success">
          <div className="flex items-start gap-2">
            <div className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Check className="w-3 h-3 text-gray-600" />
            </div>
            <div>
              <p className="text-[15px] text-gray-800">
                All set! Your monitor is now active.
              </p>
              <p className="text-[13px] text-gray-500 mt-0.5">
                {submittedConfig}
              </p>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex gap-2 mt-3">
            <button
              onClick={() => openSettings('monitors')}
              className="px-4 py-2 text-[13px] font-medium text-white bg-gray-900 hover:bg-gray-800 rounded-lg transition-all active:scale-[0.98]"
            >
              See Monitor
            </button>
            {/* Done button - only show in 'done' step, not 'completed' */}
            {step === 'done' && (
              <button
                onClick={() => {
                  setStep('completed');
                  onComplete(true);
                }}
                className="px-4 py-2 text-[13px] font-medium text-gray-600 hover:text-gray-900 transition-colors"
              >
                Done
              </button>
            )}
          </div>
        </AssistantMessage>
      )}

      {/* Message 7: Error state (visible when error) */}
      {step === 'error' && errorInfo && (
        <AssistantMessage delay={150} messageId="error">
          <div className="flex items-start gap-2">
            <div className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0 mt-0.5">
              <AlertCircle className="w-3 h-3 text-gray-500" />
            </div>
            <div>
              <p className="text-[15px] text-gray-800">
                {errorInfo.message}
              </p>
              {errorInfo.details && (
                <p className="text-[13px] text-gray-500 mt-0.5">
                  {errorInfo.details}
                </p>
              )}
            </div>
          </div>

          {/* Retry/Cancel buttons */}
          <div className="flex gap-2 mt-3">
            {parseError({ message: errorInfo.message }).requiresUpgrade ? (
              <button
                onClick={() => openSettings('billing')}
                className="flex items-center gap-1.5 px-4 py-2 text-[13px] font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition-all active:scale-[0.98]"
              >
                Upgrade Plan
              </button>
            ) : (
              <button
                onClick={handleRetry}
                className="flex items-center gap-1.5 px-4 py-2 text-[13px] font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition-all active:scale-[0.98]"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                Try Again
              </button>
            )}
            <button
              onClick={onCancel}
              className="px-4 py-2 text-[13px] font-medium text-gray-600 hover:text-gray-900 transition-colors"
            >
              Cancel
            </button>
          </div>
        </AssistantMessage>
      )}

      {/* Scroll anchor */}
      <div ref={bottomRef} />

      <style>{`
        @keyframes messageIn {
          0% {
            opacity: 0;
            transform: translateY(12px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes bounceDot {
          0%, 60%, 100% {
            transform: translateY(0);
          }
          30% {
            transform: translateY(-4px);
          }
        }

        .animate-message-in {
          animation: messageIn 400ms cubic-bezier(0.16, 1, 0.3, 1) both;
        }

        .animate-fade-in {
          animation: fadeIn 200ms ease-out both;
        }

        .animate-bounce-dot {
          animation: bounceDot 1.4s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default MonitorSetupFlow;
